import React, { useState } from 'react';

import { Alert, DialogActions, Grid, InputLabel, ListItemText, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import {
  AlineButton,
  AlineDialog,
  CheckMarkMenuItem,
  FormikAlineInput,
  NotEnoughSeatsWarning,
  ToolbarInput,
  ToolbarSelect,
} from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { emailWithSubject } from 'src/helpers';
import { useAddOrgMember, useGetOrgMembers } from 'src/hooks';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import * as Yup from 'yup';

interface UserDialogValues {
  email: string;
  firstName: string;
  jobTitle: string;
  lastName: string;
  role: Apollo.OrgRole;
}

const UserDialogSchema = Yup.object().shape({
  activationStatus: Yup.boolean().oneOf([true]),
  email: Yup.string().email('Please enter a valid email').required('Email required'),
  firstName: Yup.string(),
  jobTitle: Yup.string(),
  lastName: Yup.string(),
});

const useStyles = makeStyles()(theme => ({
  changeEmailWrapper: {
    alignItems: 'baseline',
    display: 'flex',
  },
  dialog: {
    width: 566,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  leftGridItem: {
    padding: theme.spacing(0, 1, 3, 0),
  },
  noteContainer: {
    marginBottom: theme.spacing(2),
  },
  rightGridItem: {
    padding: theme.spacing(0, 0, 3, 1),
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
  supportText: {
    ...theme.appTypography.buttonSmall,
    cursor: 'pointer',
    marginLeft: theme.spacing(2),
  },
}));

function EmailInputLabel({ user }: { user?: Pick<Apollo.User, 'id' | '__typename'> }): JSX.Element {
  const { classes } = useStyles();

  if (!user) return <InputLabel className={classes.inputLabel}>Email</InputLabel>;

  return (
    <div className={classes.changeEmailWrapper}>
      <InputLabel className={classes.inputLabel}>Email</InputLabel>
      <Typography
        className={classes.supportText}
        onClick={() => emailWithSubject({ subject: 'Aline Support Request: Change Email' })}
      >
        Contact support to change
      </Typography>
    </div>
  );
}

type UserDialogUser = Pick<
  Apollo.User,
  'email' | 'firstName' | 'id' | 'jobTitle' | 'lastName' | 'role' | '__typename' | 'isDeactivated'
>;

interface UserProfileDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user?: UserDialogUser;
}

export function UserProfileDialog(props: UserProfileDialogProps): JSX.Element {
  const { open, setOpen, user } = props;

  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const currentUser = useSelectCurrentUser();
  const { addOrgMember } = useAddOrgMember();
  const [updateUser] = Apollo.useUpdateUserMutation();
  const [selectOpen, setSelectOpen] = useState<boolean>(false);
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.All } });
  const admins = orgMembers.filter(user => user.role === Apollo.OrgRole.Admin);
  const isCurrentUserAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  // There needs to be at least 1 admin in an Org and if there is 1 admin left, (s)he shouldn't be able to change roles.
  const isLastOrgAdminRemaining =
    user && admins.length <= 1 && admins.some(admin => admin.id === user.id);
  const isRoleSelectDisabled = !isCurrentUserAdmin || isLastOrgAdminRemaining;

  async function createOrEditUser(userProps: UserDialogValues): Promise<void> {
    if (user) {
      const updateProps = getUserUpdateProps(userProps, { isAdmin: isCurrentUserAdmin });
      const { data } = await updateUser({ variables: { userId: user.id, userProps: updateProps } });
      if (data)
        dispatch({
          payload: {
            key: uuid4(),
            message: 'User successfully updated.',
            variant: 'success',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      return;
    }

    if (!currentUser?.org) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Must create an org before adding new users.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    const { data } = await addOrgMember(currentUser.org.id, {
      variables: { input: { orgId: currentUser.org.id, props: userProps } },
    });
    if (data)
      dispatch({
        payload: {
          key: uuid4(),
          message: 'User successfully created.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
  }

  return (
    <AlineDialog
      classes={{ paper: classes.dialog }}
      contentPaddingPx={0}
      open={open}
      title={user ? 'Edit user' : 'New user'}
      onClose={() => setOpen(false)}
    >
      <div className={classes.dialogContent}>
        {user && <UserAccountStatus user={user} />}
        <Formik<UserDialogValues>
          initialValues={{
            email: user?.email || '',
            firstName: user?.firstName || '',
            jobTitle: user?.jobTitle || '',
            lastName: user?.lastName || '',
            role: user?.role || currentUser?.org?.orgDefaultRole || Apollo.OrgRole.Full,
          }}
          validationSchema={UserDialogSchema}
          onSubmit={async (userProps, { setSubmitting }): Promise<void> => {
            await createOrEditUser(userProps);
            setSubmitting(false);
            setOpen(false);
          }}
        >
          {({ dirty, handleReset, handleSubmit, isSubmitting, values }) => (
            <>
              <NotEnoughSeatsWarning selectedRole={values.role} />
              <form onReset={handleReset} onSubmit={handleSubmit}>
                <Grid container>
                  <Grid className={classes.leftGridItem} size={6}>
                    <InputLabel className={classes.inputLabel}>First name</InputLabel>
                    <FormikAlineInput disabled={user?.isDeactivated} fieldName="firstName" />
                  </Grid>
                  <Grid className={classes.rightGridItem} size={6}>
                    <InputLabel className={classes.inputLabel}>Last name</InputLabel>
                    <FormikAlineInput disabled={user?.isDeactivated} fieldName="lastName" />
                  </Grid>
                  <Grid className={classes.leftGridItem} size={6}>
                    <InputLabel className={classes.inputLabel}>Job title</InputLabel>
                    <FormikAlineInput disabled={user?.isDeactivated} fieldName="jobTitle" />
                  </Grid>
                  <Grid className={classes.rightGridItem} size={6}>
                    <EmailInputLabel user={user} />
                    <FormikAlineInput disabled={!!user} fieldName="email" />
                  </Grid>
                  <Grid className={classes.leftGridItem} size={6}>
                    <InputLabel className={classes.inputLabel}>Role</InputLabel>
                    <Field disabled={user?.isDeactivated} name="role">
                      {({
                        field: { onChange, value, ...restField },
                      }: FieldProps<Apollo.OrgRole, UserDialogValues>) => (
                        <ToolbarSelect
                          customRenderValue={capitalCase(value)}
                          disabled={isRoleSelectDisabled || user?.isDeactivated}
                          input={<ToolbarInput fullWidth open={selectOpen} />}
                          menuAttribute="user-role-menu"
                          open={selectOpen}
                          selectAttribute="user-role-select"
                          spanAttribute="user-role-span"
                          value={value}
                          onClose={() => setSelectOpen(false)}
                          onOpen={() => setSelectOpen(true)}
                          onSelectChange={event => {
                            event.stopPropagation();
                            onChange(event);
                          }}
                          {...restField}
                        >
                          {[
                            Apollo.OrgRole.Admin,
                            Apollo.OrgRole.Full,
                            Apollo.OrgRole.Basic,
                            Apollo.OrgRole.Viewer,
                          ].map((role, index) => {
                            const roleText = capitalCase(role);

                            return (
                              // value prop required for selected state to work
                              <CheckMarkMenuItem key={index} selected={value === role} value={role}>
                                <ListItemText primary={roleText} />
                              </CheckMarkMenuItem>
                            );
                          })}
                        </ToolbarSelect>
                      )}
                    </Field>
                  </Grid>
                </Grid>
                <DialogActions>
                  <AlineButton color="primary" variant="outlined" onClick={() => setOpen(false)}>
                    Cancel
                  </AlineButton>
                  <AlineButton
                    className={classes.saveButton}
                    color="primary"
                    disabled={!dirty || isSubmitting}
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    Save
                  </AlineButton>
                </DialogActions>
              </form>
            </>
          )}
        </Formik>
      </div>
    </AlineDialog>
  );
}

function getUserUpdateProps(userProps: UserDialogValues, options: { isAdmin: boolean }) {
  if (options.isAdmin) return userProps;

  const { email, firstName, jobTitle, lastName } = userProps;
  return { email, firstName, jobTitle, lastName };
}

const useAlertStyles = makeStyles()(_theme => ({
  action: {
    alignItems: 'center',
  },
}));

function UserAccountStatus({ user }: { user: UserDialogUser }) {
  const { classes } = useAlertStyles();
  const [updateUserActivation, { loading }] = Apollo.useUpdateUserActivationMutation();

  if (!user.isDeactivated) return null;

  return (
    <Alert
      action={
        <AlineButton
          loading={loading}
          onClick={async () => {
            await updateUserActivation({
              variables: {
                isDeactivated: false,
                userId: user.id,
              },
            });
          }}
        >
          Reactivate
        </AlineButton>
      }
      classes={{ action: classes.action }}
      severity="warning"
    >
      User is deactivated. Please reactivate to edit.
    </Alert>
  );
}
